import * as THREE from 'three';
import { CinematicChapterScene } from './CinematicChapterScene';
import { QUALITY_PROFILES, chooseAutomaticQuality } from './quality';
import type {
  ChapterId,
  CinematicEngineOptions,
  EngineDiagnostics,
  QualityTier,
  TimelineState,
} from './types';
import { CHAPTER_PRESETS, CHAPTER_STARTS, FILM_DURATION, PRESET_BY_ID } from '../scenes/presets';

interface RenderBridgeTarget {
  __VEIL_RENDER__?: {
    ready: Promise<void>;
    duration: number;
    seek(seconds: number): Promise<void>;
    diagnostics(): EngineDiagnostics;
  };
}

export class CinematicEngine {
  readonly renderer: THREE.WebGLRenderer;
  readonly whenReady: Promise<void>;
  readonly duration = FILM_DURATION;
  private readonly chapters = new Map<ChapterId, CinematicChapterScene>();
  private readonly overlayScene = new THREE.Scene();
  private readonly overlayCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly overlayMaterial: THREE.ShaderMaterial;
  private readonly renderTarget: THREE.WebGLRenderTarget;
  private readonly drawingBufferSize = new THREE.Vector2();
  private active?: CinematicChapterScene;
  private quality: QualityTier;
  private reducedMotion: boolean;
  private width = 1;
  private height = 1;
  private disposed = false;

  constructor(options: CinematicEngineOptions) {
    this.quality = options.quality ?? chooseAutomaticQuality();
    this.reducedMotion = options.reducedMotion ?? false;
    const offlineReadback = typeof window !== 'undefined'
      && new URLSearchParams(window.location.search).get('offline-render') === '1';
    this.renderer = new THREE.WebGLRenderer({
      canvas: options.canvas,
      antialias: true,
      alpha: options.alpha ?? false,
      powerPreference: options.powerPreference ?? 'high-performance',
      // Interactive playback avoids this expensive buffer. Deterministic
      // frame export explicitly opts in so canvas readback cannot return a
      // compositor-cleared black frame.
      preserveDrawingBuffer: offlineReadback,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.shadowMap.enabled = QUALITY_PROFILES[this.quality].shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.setClearColor(0x050507, options.alpha ? 0 : 1);
    this.renderer.autoClear = false;
    this.renderTarget = new THREE.WebGLRenderTarget(1, 1, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      depthBuffer: true,
      stencilBuffer: false,
    });
    this.renderTarget.texture.name = 'cinematic-color-buffer';

    for (const preset of CHAPTER_PRESETS) {
      this.chapters.set(preset.id, new CinematicChapterScene(preset, QUALITY_PROFILES[this.quality]));
    }
    this.overlayMaterial = this.createOverlayMaterial();
    const overlay = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.overlayMaterial);
    overlay.frustumCulled = false;
    this.overlayScene.add(overlay);

    this.whenReady = Promise.all([...this.chapters.values()].map((chapter) => chapter.load())).then(() => undefined);
  }

  /** Evaluate and draw exactly one frame from absolute timeline state. */
  render(state: TimelineState): void {
    if (this.disposed) throw new Error('CinematicEngine has been disposed.');
    const chapter = this.chapters.get(state.chapterId);
    if (!chapter) throw new Error(`Unknown cinematic chapter: ${state.chapterId}`);
    if (this.active !== chapter) {
      const previous = this.active?.id;
      this.active?.exit(chapter.id);
      this.active = chapter;
      chapter.enter(previous);
    }

    chapter.evaluate({ ...state, reducedMotion: state.reducedMotion ?? this.reducedMotion });
    this.renderer.toneMappingExposure = chapter.scene.userData.exposure ?? 1;
    this.overlayMaterial.uniforms.uTime!.value = state.absoluteTime;
    this.overlayMaterial.uniforms.uResolution!.value.set(this.width, this.height);
    this.overlayMaterial.uniforms.uGrain!.value = this.reducedMotion ? 0.001 : 0.0035;
    this.overlayMaterial.uniforms.uWarmth!.value = state.chapterId === 'garden-reopened' || state.chapterId === 'eyes-opened' ? 0.045 : 0.012;
    const golgothaRoad = state.chapterId === 'golgotha' && state.absoluteTime < 181;
    const judgmentCourt = state.chapterId === 'garden-and-judgment' && state.absoluteTime >= 131;
    this.overlayMaterial.uniforms.uContrast!.value = state.chapterId === 'veil'
      ? 0.965
      : judgmentCourt || golgothaRoad
        ? 1.005
        : state.chapterId === 'bread-and-night' || state.chapterId === 'garden-and-judgment'
        ? 0.975
      : state.chapterId === 'silence'
        ? 1.035
        : 1.045;
    this.overlayMaterial.uniforms.uShadowLift!.value = state.chapterId === 'veil'
      ? 0.015
      : judgmentCourt
        ? 0.005
        : state.chapterId === 'bread-and-night' || state.chapterId === 'garden-and-judgment'
        ? 0.012
        : golgothaRoad
          ? 0.012
          : 0;
    this.overlayMaterial.uniforms.uSaturation!.value = state.chapterId === 'silence'
      ? 0.82
      : state.chapterId === 'garden-reopened' || state.chapterId === 'eyes-opened'
        ? 1.04
        : 0.94;

    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.clear(true, true, true);
    this.renderer.render(chapter.scene, chapter.camera);
    this.renderer.setRenderTarget(null);
    this.renderer.clear(true, true, true);
    this.renderer.render(this.overlayScene, this.overlayCamera);
  }

  /** Render by whole-film seconds. Used by Director seeking and offline frame export. */
  async seekFilmTime(seconds: number): Promise<void> {
    await this.whenReady;
    const time = Math.min(Math.max(0, seconds), FILM_DURATION);
    let preset = CHAPTER_PRESETS[CHAPTER_PRESETS.length - 1]!;
    for (const candidate of CHAPTER_PRESETS) {
      const start = CHAPTER_STARTS[candidate.id];
      if (time >= start && (time < start + candidate.duration || candidate === CHAPTER_PRESETS[CHAPTER_PRESETS.length - 1])) {
        preset = candidate;
        break;
      }
    }
    this.render({
      chapterId: preset.id,
      absoluteTime: time,
      localTime: Math.min(preset.duration, time - CHAPTER_STARTS[preset.id]),
      chapterDuration: preset.duration,
      reducedMotion: this.reducedMotion,
    });
  }

  resize(width: number, height: number, updateStyle = false): void {
    this.width = Math.max(1, Math.round(width));
    this.height = Math.max(1, Math.round(height));
    const profile = QUALITY_PROFILES[this.quality];
    const deviceRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio;
    this.renderer.setPixelRatio(Math.min(deviceRatio, profile.pixelRatioCap));
    this.renderer.setSize(this.width, this.height, updateStyle);
    this.renderer.getDrawingBufferSize(this.drawingBufferSize);
    this.renderTarget.setSize(this.drawingBufferSize.x, this.drawingBufferSize.y);
    for (const chapter of this.chapters.values()) chapter.resize(this.width, this.height);
  }

  setQuality(tier: QualityTier): void {
    this.quality = tier;
    const profile = QUALITY_PROFILES[tier];
    this.renderer.shadowMap.enabled = profile.shadows;
    for (const chapter of this.chapters.values()) chapter.setQuality(profile);
    this.resize(this.width, this.height, false);
  }

  setReducedMotion(reduced: boolean): void {
    this.reducedMotion = reduced;
  }

  diagnostics(): EngineDiagnostics {
    const info = this.renderer.info;
    return {
      chapterId: this.active?.id,
      triangles: info.render.triangles,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      programs: info.programs?.length ?? 0,
      quality: this.quality,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.active?.exit();
    for (const chapter of this.chapters.values()) chapter.dispose();
    this.chapters.clear();
    (this.overlayScene.children[0] as THREE.Mesh).geometry.dispose();
    this.overlayMaterial.dispose();
    this.renderTarget.dispose();
    this.renderer.dispose();
    this.disposed = true;
  }

  private createOverlayMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        tScene: { value: this.renderTarget.texture },
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(1, 1) },
        uGrain: { value: 0.0035 },
        uWarmth: { value: 0.012 },
        uContrast: { value: 1.045 },
        uSaturation: { value: 0.94 },
        uShadowLift: { value: 0 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tScene;
        uniform float uTime;
        uniform vec2 uResolution;
        uniform float uGrain;
        uniform float uWarmth;
        uniform float uContrast;
        uniform float uSaturation;
        uniform float uShadowLift;
        varying vec2 vUv;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        void main() {
          vec2 texel = 1.0 / max(uResolution, vec2(1.0));
          vec2 aberration = texel * 0.7;
          vec3 color;
          color.r = texture2D(tScene, vUv + aberration).r;
          color.g = texture2D(tScene, vUv).g;
          color.b = texture2D(tScene, vUv - aberration).b;

          // A restrained four-tap halation softens only bright practicals.
          vec3 blur = texture2D(tScene, vUv + vec2(texel.x * 2.0, 0.0)).rgb;
          blur += texture2D(tScene, vUv - vec2(texel.x * 2.0, 0.0)).rgb;
          blur += texture2D(tScene, vUv + vec2(0.0, texel.y * 2.0)).rgb;
          blur += texture2D(tScene, vUv - vec2(0.0, texel.y * 2.0)).rgb;
          blur *= 0.25;
          float highlight = smoothstep(0.58, 1.0, dot(blur, vec3(0.2126, 0.7152, 0.0722)));
          color += blur * highlight * 0.055;

          float luminance = dot(color, vec3(0.2126, 0.7152, 0.0722));
          // Preserve plate shadow detail: a gentle film toe instead of a hard
          // contrast pivot, which would crush candlelit interiors.
          color = pow(max(color, vec3(0.0)), vec3(0.96));
          color += vec3(0.002, 0.003, 0.005) * (1.0 - smoothstep(0.08, 0.48, luminance));
          float shadowMask = 1.0 - smoothstep(0.025, 0.3, luminance);
          color += vec3(0.78, 0.88, 1.0) * uShadowLift * shadowMask;
          color += vec3(uWarmth, uWarmth * 0.42, -uWarmth * 0.18) * smoothstep(0.3, 0.9, luminance);

          // A chapter-aware print-light pass gives the geometry a deliberate
          // filmic separation without crushing candlelit faces.  Apply it
          // after the gentle toe/shoulder work so black cloth retains detail.
          color = (color - vec3(0.5)) * uContrast + vec3(0.5);
          float gradedLuma = dot(color, vec3(0.2126, 0.7152, 0.0722));
          color = mix(vec3(gradedLuma), color, uSaturation);

          vec2 centered = vUv - 0.5;
          centered.x *= uResolution.x / max(1.0, uResolution.y);
          float vignette = smoothstep(0.42, 0.96, length(centered));
          float frame = floor(uTime * 24.0 + 0.5);
          float grain = (hash(floor(gl_FragCoord.xy * 0.5) + frame) - 0.5) * uGrain;
          color *= 1.0 - vignette * 0.1;
          color += grain;
          gl_FragColor = vec4(max(color, vec3(0.0)), 1.0);
          #include <colorspace_fragment>
        }
      `,
      transparent: false,
      depthTest: false,
      depthWrite: false,
    });
  }
}

/** Install the stable frame-export hook without forcing a global side effect on import. */
export function installDeterministicRenderBridge(
  engine: CinematicEngine,
  target: RenderBridgeTarget = window as unknown as RenderBridgeTarget,
): () => void {
  target.__VEIL_RENDER__ = {
    ready: engine.whenReady,
    duration: engine.duration,
    seek: (seconds: number) => engine.seekFilmTime(seconds),
    diagnostics: () => engine.diagnostics(),
  };
  return () => {
    delete target.__VEIL_RENDER__;
  };
}

export function timelineStateAt(seconds: number, reducedMotion = false): TimelineState {
  const time = Math.min(Math.max(0, seconds), FILM_DURATION);
  const preset = CHAPTER_PRESETS.find((chapter) => {
    const start = CHAPTER_STARTS[chapter.id];
    return time >= start && time < start + chapter.duration;
  }) ?? PRESET_BY_ID['eyes-opened'];
  return {
    chapterId: preset.id,
    absoluteTime: time,
    localTime: Math.min(preset.duration, time - CHAPTER_STARTS[preset.id]),
    chapterDuration: preset.duration,
    reducedMotion,
  };
}
